import { Form, Input, Select } from 'antd';
import dompurify from 'dompurify';
import gql from 'graphql-tag';
import { useEffect, useState } from 'react';
import { withApollo } from 'react-apollo';
import { queryReferralQuestionsByCompanyId } from 'src/_shared/api/graphql/custom/referral-questions/';
import { fetchModernFormQuestions, ml } from 'src/_shared/services/utils';

const { TextArea } = Input;
const sanitizer = dompurify.sanitize;

const ReferralQuestionsDetailsComponent = ({
	allMultiLingualData,
	candidateQuestionsData,
	currentUser,
	questionsData,
	editing,
	onValuesChange,
	client,
	referralUserGroupId,
}) => {
	const [form] = Form.useForm();
	const [formValues, setFormValues] = useState({});
	const [dropdownOptions, setDropdownOptions] = useState({});
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const fetchQuestionOptions = async () => {
			try {
				setLoading(true);
				const options = {};

				// Helper to extract options from a question object
				const extractOptions = (questionData) => {
					const fieldKey = questionData.field_name || questionData.name;
					let questionOptions = questionData.options;

					if (typeof questionOptions === 'string') {
						try {
							questionOptions = JSON.parse(questionOptions);
						} catch {
							questionOptions = null;
						}
					}

					if (
						fieldKey &&
						Array.isArray(questionOptions) &&
						questionOptions.length > 0
					) {
						const mappedOptions = questionOptions.map((opt) => {
							if (typeof opt === 'string') {
								return {
									label: opt,
									value: opt.toLowerCase().replace(/\s+/g, '_'),
								};
							}
							return {
								label: opt.text || opt.label || opt.value,
								value: opt.value || opt.key,
							};
						});
						options[fieldKey] = mappedOptions;
						if (questionData.id) {
							options[questionData.id] = mappedOptions;
						}

						if (questionData.label) {
							options[questionData.label] = mappedOptions;
						}
					}
				};

				// Try fetching modern form questions from S3 for all form types
				const formTypes = ['referral', 'acceptance', 'interested'];
				for (const formType of formTypes) {
					try {
						const userForQuestions = referralUserGroupId
							? { ...currentUser, userGroupId: referralUserGroupId }
							: currentUser;
						const modernQuestions = await fetchModernFormQuestions(
							userForQuestions,
							formType,
							null,
							client
						);
						if (modernQuestions && modernQuestions.length > 0) {
							for (const item of modernQuestions) {
								const questionData =
									typeof item.questions === 'string'
										? JSON.parse(item.questions)
										: item.questions || item;
								if (
									questionData.element === 'Dropdown' ||
									questionData.options
								) {
									extractOptions(questionData);
								}
							}
						}
					} catch (modernError) {
						console.error(
							`Error fetching modern ${formType} form questions:`,
							modernError
						);
					}
				}

				// Also fetch legacy questions as fallback
				try {
					const response = await client.query({
						query: gql(queryReferralQuestionsByCompanyId),
						variables: {
							companyId: currentUser.companyId,
							first: 100,
						},
					});

					for (const item of response.data.queryReferralQuestionsByCompanyId
						.items) {
						if (item.questions) {
							try {
								const questionData =
									typeof item.questions === 'string'
										? JSON.parse(item.questions)
										: item.questions;
								if (
									questionData.element === 'Dropdown' ||
									questionData.options
								) {
									extractOptions(questionData);
								}
							} catch (error) {
								console.error('Error parsing question options:', error);
							}
						}
					}
				} catch (legacyError) {
					console.error('Error fetching legacy questions:', legacyError);
				}

				setDropdownOptions(options);
			} catch (error) {
				console.error('Error fetching question options:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchQuestionOptions();
	}, [client, currentUser.companyId, referralUserGroupId]);

	useEffect(() => {
		if (editing) {
			const allQuestions = [
				...(questionsData &&
				typeof questionsData === 'string' &&
				questionsData.length > 0
					? JSON.parse(questionsData)
					: []),
				...(candidateQuestionsData &&
				typeof candidateQuestionsData === 'string' &&
				candidateQuestionsData.length > 0
					? JSON.parse(candidateQuestionsData)
					: []),
			];

			const initialValues = allQuestions.reduce((acc, element) => {
				acc[element.name] = element.value;
				return acc;
			}, {});

			setFormValues(initialValues);
			form.setFieldsValue(initialValues);
		}
	}, [editing, questionsData, candidateQuestionsData]);

	const handleFormChange = (changedValues, allValues) => {
		// Preserve any existing _text values from previous state
		const textValues = {};
		for (const key of Object.keys(formValues)) {
			if (key.endsWith('_text')) {
				textValues[key] = formValues[key];
			}
		}
		const mergedValues = { ...textValues, ...allValues };
		setFormValues(mergedValues);
		if (onValuesChange) {
			onValuesChange(changedValues, mergedValues);
		}
	};

	const findOptionsForElement = (element) =>
		dropdownOptions[element.name] ||
		dropdownOptions[element.id] ||
		dropdownOptions[element.question] ||
		[];

	const getDropdownDisplayValue = (element) => {
		const options = findOptionsForElement(element);
		const option = options.find((opt) => opt.value === element.value);
		return option ? option.label : element.text || element.value;
	};

	const getInputComponent = (element) => {
		const { name, text } = element;

		if (name.startsWith('dropdown_')) {
			const options = findOptionsForElement(element);
			// If no options available from form builder, fall back to text input
			if (options.length === 0) {
				return <Input placeholder={text} />;
			}
			return (
				<Select
					style={{ width: '100%' }}
					options={options}
					loading={loading}
					placeholder="Select an option"
					onChange={(value) => {
						// Find the selected option to get the display text
						const selectedOption = options.find((opt) => opt.value === value);
						const selectedText = selectedOption?.label || value;

						// Update form field
						form.setFieldValue(name, value);

						// Build complete values including all _text fields
						const currentFormValues = form.getFieldsValue();
						const textValues = {};
						for (const key of Object.keys(formValues)) {
							if (key.endsWith('_text')) {
								textValues[key] = formValues[key];
							}
						}
						const allValuesWithText = {
							...textValues,
							...currentFormValues,
							[`${name}_text`]: selectedText,
						};

						// Update local state and notify parent directly
						setFormValues(allValuesWithText);
						if (onValuesChange) {
							onValuesChange(
								{ [name]: value, [`${name}_text`]: selectedText },
								allValuesWithText
							);
						}
					}}
				/>
			);
		}

		if (name.startsWith('text_area_')) {
			return <TextArea rows={4} />;
		}

		return <Input />;
	};

	const renderQuestions = (data, isReferralQuestion = false) => {
		if (!data) return null;

		const parsedData = JSON.parse(data);
		if (!parsedData || !Array.isArray(parsedData)) return null;

		return parsedData.map((element, i) => (
			<div key={element.name} className="rpf-qa">
				<div
					className="qa-row"
					style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}
				>
					{isReferralQuestion ? (
						<>
							<label style={{ flexShrink: 0 }}>{`Ques${i + 1}`}.</label>
							<span
								dangerouslySetInnerHTML={{
									__html: sanitizer(element.question),
								}}
								style={{
									whiteSpace: 'pre-line',
									flex: 1,
								}}
							/>
						</>
					) : (
						<>
							<label
								dangerouslySetInnerHTML={{
									__html: sanitizer(element.question),
								}}
								className="qa-label"
							/>
							<br />
						</>
					)}
				</div>
				<div
					className="qa-row"
					style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}
				>
					{isReferralQuestion && <label>Ans.</label>}
					{editing ? (
						<div style={{ flex: 1 }}>
							<Form.Item name={element.name} style={{ marginBottom: 0 }}>
								{getInputComponent(element)}
							</Form.Item>
						</div>
					) : (
						<span
							style={{
								whiteSpace: 'pre-line',
								flex: 1,
							}}
						>
							{element.name.startsWith('dropdown_')
								? getDropdownDisplayValue(element)
								: element.text || element.value}
						</span>
					)}
				</div>
			</div>
		));
	};

	const questions =
		questionsData && questionsData.length > 0
			? JSON.parse(questionsData)
			: null;
	const candidateQuestions =
		candidateQuestionsData && candidateQuestionsData.length > 0
			? JSON.parse(candidateQuestionsData)
			: null;

	const content = (
		<>
			<div>
				{questions && questions.length > 0 && (
					<>
						<h4 className="content-right-heading">
							{ml('Referral Questions', currentUser, allMultiLingualData)}
						</h4>
						{renderQuestions(questionsData, true)}
					</>
				)}
			</div>
			{candidateQuestions && candidateQuestions.length > 0 && (
				<>
					<h4 className="content-right-heading">
						{ml('Candidate Questions', currentUser, allMultiLingualData)}
					</h4>
					{renderQuestions(candidateQuestionsData)}
				</>
			)}
		</>
	);

	return editing ? (
		<Form
			form={form}
			initialValues={formValues}
			onValuesChange={handleFormChange}
		>
			{content}
		</Form>
	) : (
		content
	);
};

export default withApollo(ReferralQuestionsDetailsComponent);
